import React, { useMemo, useState } from "react";

import {
  faCheck,
  faCompressAlt,
  faExpandAlt,
  faFilePdf,
  faListCheck,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Paper } from "@material-ui/core";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Stepper from "@material-ui/core/Stepper";
import Typography from "@material-ui/core/Typography";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  matchPath,
  Redirect,
  Switch,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import styled from "styled-components/macro";

import { BillingDocumentAction, BillingRunAction } from "actions";

import { FeatureRoute } from "components/Authorization/FeatureRoute";
import { Export } from "components/BillingWorkFlow/Export";
import { MobileBillingWorkflow } from "components/BillingWorkFlow/Mobile";
import { Overview } from "components/BillingWorkFlow/Overview";
import { Review } from "components/BillingWorkFlow/Review";
import { Send } from "components/BillingWorkFlow/Send";
import { Sundries } from "components/BillingWorkFlow/Sundries";
import { MultiButton } from "components/Button";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";

import { BillingScreens } from "constants/billing";
import { BillingRunStatus } from "constants/billingRuns";
import { ApiModel } from "constants/loading";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { SaleType } from "constants/sale";

import { ForNotReferenceSale } from "containers/ForSaleType";

import {
  billingDocumentCanBulkAcceptWithoutReversal,
  billingDocumentHasChangesToReject,
  bulkAcceptWithoutReversalBlockedCounts,
  bulkAcceptWithoutReversalBlockedMessage,
} from "lib/billingDocumentChanges";
import {
  getBillingSubPageUrl,
  getLivestockSaleId,
  getSaleRoute,
  openRCTIStatementReport,
} from "lib/navigation";
import toast from "lib/toast";

import {
  getActiveBillingRunId,
  getBillingRunById,
  getBillingRuns,
  getIsBillingRunCheckingForChanges,
  getBillingRunHasFinancialByBillingRunId,
  getIsFetchingPayments,
  getIsStaff,
  selectLedgerEntriesByDocumentId,
  selectPaymentIdsByBillingDocumentIdLookup,
  getCurrentSale,
  selectRCTIStatementBusinesses,
  getActiveRole,
  getBillingSettingsByBillingRunId,
} from "selectors";

import appHistory from "appHistory";
import {
  useBoolean,
  useHasManualChargesPermission,
  useMountEffect,
} from "hooks";
import { useIsMobile } from "hooks/useScreenSize";
import { useToggleFullScreen } from "hooks/useToggleFullscreen";
import { useWaityLoadyMultiButton } from "hooks/useWaityLoadyMultiButton";

const noBillingRunLabel = {
  disabled: true,
};

const changesDetectedLabel = {
  optional: (
    <Typography variant="caption" color="error">
      Changes Detected
    </Typography>
  ),
  error: true,
};

const getSteps = hasManualChargesPermission =>
  [
    {
      label: "Overview",
      path: BillingScreens.OVERVIEW,
    },
    hasManualChargesPermission && {
      label: "Sundries",
      path: BillingScreens.SUNDRIES,
    },
    {
      label: "Review",
      path: BillingScreens.REVIEW,
      getLabelProps: (billingRun, { hasFinancialChanges } = {}) => {
        if (isEmpty(billingRun)) {
          return noBillingRunLabel;
        }
        if (hasFinancialChanges) {
          return changesDetectedLabel;
        }
      },
    },
    {
      label: "Send",
      path: BillingScreens.SEND,
      getLabelProps: billingRun => {
        if (isEmpty(billingRun)) {
          return noBillingRunLabel;
        }
      },
    },
    {
      label: "Export",
      path: BillingScreens.EXPORT,
      getLabelProps: billingRun => {
        if (isEmpty(billingRun)) {
          return noBillingRunLabel;
        }
      },
    },
  ].filter(Boolean);

const Actions = styled.div`
  width: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BillingWorkFlow = () => {
  const isMobile = useIsMobile();
  if (isMobile) {
    return <MobileBillingWorkflow />;
  }
  return <DesktopBillingWorkflow />;
};

const DesktopBillingWorkflow = () => {
  const dispatch = useDispatch();

  const [selectedBillingDocuments, setSelectedBillingDocuments] = useState([]);

  const hasManualChargesPermission = useHasManualChargesPermission();

  const steps = useMemo(
    () => getSteps(hasManualChargesPermission),
    [hasManualChargesPermission],
  );

  const [
    isConfirmCosmeticDialogOpen,
    openConfirmCosmeticDialog,
    closeConfirmCosmeticDialog,
  ] = useBoolean();

  const [isConfirmAllDialogOpen, openConfirmAllDialog, closeConfirmAllDialog] =
    useBoolean();

  const [
    isConfirmRejectCosmeticDialogOpen,
    openConfirmRejectCosmeticDialog,
    closeConfirmRejectCosmeticDialog,
  ] = useBoolean();

  const [
    isConfirmRejectAllDialogOpen,
    openConfirmRejectAllDialog,
    closeConfirmRejectAllDialog,
  ] = useBoolean();

  const [isFullScreen, toggleFullScreen] = useToggleFullScreen();

  const isStaff = useSelector(getIsStaff);

  const ledgerEntriesByDocumentId = useSelector(
    selectLedgerEntriesByDocumentId,
  );

  const paymentIdsByBillingDocumentId = useSelector(
    selectPaymentIdsByBillingDocumentIdLookup,
  );
  const isFetchingPayments = useSelector(getIsFetchingPayments);

  const billingPath = `${getSaleRoute(":saleyard", ":saleId")}/billing`;

  const billingRunId = useSelector(getActiveBillingRunId);

  const match = useRouteMatch();
  const location = useLocation();

  const isBillingEnabled = useSelector(state => state.billingRuns.isEnabled);

  const saleType = useSelector(getCurrentSale)?.sale_type;

  const isReferenceSale = saleType === SaleType.REFERENCE_SALE;

  const rctiStatementBusinesses = useSelector(selectRCTIStatementBusinesses);

  const userRole = useSelector(getActiveRole);
  const billingSettings = useSelector(
    getBillingSettingsByBillingRunId(billingRunId),
  );

  // the order of calling BillingRunAction.enable first before BillingRunAction.subscribe is important
  // because enable requests billing runs and is a much faster endpoint than subscribe's checkForChanges endpoint.
  // The components below are dependant on the billing runs being loaded and we don't want our users to wait longer
  // than they have to.
  React.useEffect(() => {
    if (!isBillingEnabled) {
      dispatch(BillingRunAction.enable(getLivestockSaleId()));
    }
  }, [isBillingEnabled, dispatch]);

  useMountEffect(() => {
    // Unsubscribe on unmount.
    return () => {
      dispatch(BillingRunAction.unsubscribe());
      dispatch(BillingRunAction.disable());
    };
  });

  function changeTabs(newScreen) {
    appHistory.push(getBillingSubPageUrl(newScreen));
  }

  const openStep = index => {
    changeTabs(steps[index].path);
  };

  const activeStep = useMemo(() => {
    // use the same mechanism that react-router uses to match paths to determine the open section.
    const matchProfile = matchPath(location.pathname, {
      path: `${billingPath}/:section`,
    });
    const match = (matchProfile && matchProfile.params) || {};

    return steps.findIndex(step => step.path === match.section) || 0;
  }, [billingPath, location.pathname, steps]);

  const billingRun = useSelector(getBillingRunById(billingRunId));
  const isFinished = billingRun?.status === BillingRunStatus.FINISHED;
  const billingRunHasFinancialChanges = useSelector(
    getBillingRunHasFinancialByBillingRunId(billingRunId),
  );

  // We fetch data on page load, so to avoid the initial isloading flash if we already have data, override it.
  const billingRunsExist = useSelector(state => {
    const billingRuns = getBillingRuns(state);
    return Object.keys(billingRuns).length > 0;
  });

  const isBillingRunCheckingForChanges = useSelector(
    getIsBillingRunCheckingForChanges,
  );

  const isReviewStep = steps[activeStep]?.path === BillingScreens.REVIEW;

  const bulkAcceptChanges = allowFinancial => {
    if (allowFinancial && isFetchingPayments) {
      toast.error("Payments are still loading, please try again in a moment");
      return;
    }
    const billingDocumentsToAccept = allowFinancial
      ? staffAcceptAllBillingDocuments
      : selectedBillingDocuments;
    dispatch(
      BillingDocumentAction.bulkAcceptChanges(
        billingDocumentsToAccept.map(doc => doc.id),
        allowFinancial,
      ),
    );
    closeConfirmAllDialog();
    closeConfirmCosmeticDialog();
  };

  const bulkRejectChanges = allowFinancial => {
    dispatch(
      BillingDocumentAction.bulkRejectChanges(
        selectedBillingDocuments.map(doc => doc.id),
        allowFinancial,
      ),
    );
    closeConfirmRejectAllDialog();
    closeConfirmRejectCosmeticDialog();
  };

  const filterForChangedBillingDocuments = billingDocument =>
    billingDocument.hasCosmeticChanges && !billingDocument.hasFinancialChange;

  const filteredBillingDocumentIds = () =>
    selectedBillingDocuments.filter(filterForChangedBillingDocuments);

  const staffRejectAllCount = selectedBillingDocuments.filter(
    billingDocumentHasChangesToReject,
  ).length;

  const staffAcceptAllBillingDocuments = selectedBillingDocuments.filter(
    billingDocument =>
      billingDocumentCanBulkAcceptWithoutReversal(
        billingDocument,
        paymentIdsByBillingDocumentId[billingDocument.id],
        ledgerEntriesByDocumentId,
      ),
  );
  const staffAcceptAllCount = staffAcceptAllBillingDocuments.length;
  const staffAcceptAllSkippedCount = staffRejectAllCount - staffAcceptAllCount;

  const onClickStaffAcceptAll = () => {
    if (staffAcceptAllCount) {
      openConfirmAllDialog();
      return;
    }
    toast.warning(
      bulkAcceptWithoutReversalBlockedMessage(
        bulkAcceptWithoutReversalBlockedCounts(
          selectedBillingDocuments,
          paymentIdsByBillingDocumentId,
          ledgerEntriesByDocumentId,
        ),
      ),
      { autoClose: false },
    );
  };

  const buttons = [
    useWaityLoadyMultiButton(
      "Re-run",
      () => BillingRunAction.checkForChanges(billingRunId),
      isBillingRunCheckingForChanges,
      true,
      !billingRunsExist || isFinished,
      isFinished
        ? "Billing Run cannot be re-run while the status is set to Finished"
        : false,
    ),
    isReviewStep &&
      filteredBillingDocumentIds().length && {
        title: `Accept (${filteredBillingDocumentIds().length}) non-financial changes`,
        icon: faCheck,
        onClick: () => openConfirmCosmeticDialog(),
      },
    isReviewStep &&
      isStaff &&
      !isFetchingPayments &&
      staffRejectAllCount && {
        title: `(Staff Only) Accept all (${staffAcceptAllCount}) changes without reversal`,
        icon: faCheck,
        onClick: onClickStaffAcceptAll,
      },
    isReviewStep &&
      filteredBillingDocumentIds().length && {
        title: `Reject (${filteredBillingDocumentIds().length}) non-financial changes`,
        icon: faTimes,
        onClick: () => openConfirmRejectCosmeticDialog(),
      },
    isReviewStep &&
      isStaff &&
      staffRejectAllCount && {
        title: `(Staff Only) Reject all (${staffRejectAllCount}) changes`,
        icon: faTimes,
        onClick: () => openConfirmRejectAllDialog(),
      },
    {
      title: isFullScreen ? "Exit Fullscreen mode" : "Enter Fullscreen mode",
      icon: isFullScreen ? faCompressAlt : faExpandAlt,
      onClick: toggleFullScreen,
    },
  ].filter(Boolean);

  if (
    [
      BillingScreens.REVIEW,
      BillingScreens.SEND,
      BillingScreens.EXPORT,
    ].includes(steps[activeStep]?.path) &&
    selectedBillingDocuments.length
  ) {
    const billingDocumentIds = selectedBillingDocuments.map(
      billingDocument => billingDocument.id,
    );
    buttons.push({
      title: `Compile (${billingDocumentIds.length}) items as PDF`,
      icon: faFilePdf,
      onClick: () =>
        dispatch(BillingDocumentAction.compileDocuments(billingDocumentIds)),
    });
  }
  const rctiPerBuyerIsEnabled =
    rctiStatementBusinesses.length > 0 && billingSettings?.splitRctiByBuyer;

  if (isReviewStep && rctiPerBuyerIsEnabled) {
    const rctiPerBuyerItems = rctiStatementBusinesses.map(business => ({
      title: business.name,
      icon: faFilePdf,
      onClick: () =>
        openRCTIStatementReport(
          business.id,
          userRole,
          billingSettings.rctiSummaryTitle || undefined,
        ),
    }));
    buttons.push({
      title: billingSettings.rctiSummaryTitle || "Vendor Summary",
      icon: faListCheck,
      subNavItems: rctiPerBuyerItems,
    });
  }

  const redirectUrl = isReferenceSale
    ? `${match.url}/review`
    : `${match.url}/overview`;

  return (
    <WaitForSync
      requiredData={[
        ApiModel.BILLING_DOCUMENTS,
        ApiModel.BILLING_RUNS,
        ApiModel.BUSINESSES,
        ApiModel.EMAIL,
        ApiModel.LEDGER_ENTRIES,
        ApiModel.MANUAL_ADJUSTMENTS,
      ]}
      forceNotLoading={billingRunsExist}
    >
      <Column full>
        <ForNotReferenceSale selectedSaleType={saleType}>
          <Paper>
            <Row>
              <Column full>
                <Stepper nonLinear activeStep={activeStep}>
                  {steps.map((step, index) => {
                    const labelProps =
                      step.getLabelProps?.(billingRun, {
                        hasFinancialChanges: billingRunHasFinancialChanges,
                      }) || {};
                    if (!labelProps.disabled) {
                      labelProps.onClick = () => openStep(index);
                    }

                    return (
                      <Step data-tour={step.path} key={step.label}>
                        <StepLabel {...labelProps}>{step.label}</StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </Column>
              <Actions>
                <Column alignCenter gridGap={1}>
                  <MultiButton buttons={buttons} />
                </Column>
              </Actions>
            </Row>
          </Paper>
        </ForNotReferenceSale>

        <Switch>
          <FeatureRoute
            roleDeploymentPermissions={[DeploymentPermission.read]}
            saleyardPermissions={[SaleyardPermission.read]}
            path={`${billingPath}/overview`}
            component={Overview}
          />
          <FeatureRoute
            exact
            roleDeploymentPermissions={[
              DeploymentPermission.featureManualCharges,
            ]}
            saleyardPermissions={[SaleyardPermission.featureManualCharges]}
            path={`${billingPath}/sundries`}
            component={Sundries}
          />
          <FeatureRoute
            roleDeploymentPermissions={[DeploymentPermission.read]}
            saleyardPermissions={[SaleyardPermission.read]}
            path={`${billingPath}/review`}
            render={() => (
              <Review
                setSelectedBillingDocuments={setSelectedBillingDocuments}
              />
            )}
          />
          <FeatureRoute
            roleDeploymentPermissions={[DeploymentPermission.read]}
            saleyardPermissions={[SaleyardPermission.read]}
            path={`${billingPath}/send`}
            render={() => (
              <Send
                setSelectedBillingDocuments={setSelectedBillingDocuments}
                billingRunId={billingRunId}
              />
            )}
          />

          <FeatureRoute
            roleDeploymentPermissions={[DeploymentPermission.featureBillingRun]}
            saleyardPermissions={[SaleyardPermission.featureBillingRun]}
            path={`${billingPath}/export`}
            render={() => (
              <Export
                setSelectedBillingDocuments={setSelectedBillingDocuments}
                billingRunId={billingRunId}
              />
            )}
          />

          <Redirect to={redirectUrl} />
        </Switch>
      </Column>
      <ConfirmDialog
        title="Accept non-financial changes"
        message={`Please confirm you'd like to accept ${filteredBillingDocumentIds().length} non-financial changes`}
        isOpen={isConfirmCosmeticDialogOpen}
        onCancel={closeConfirmCosmeticDialog}
        onDelete={() => bulkAcceptChanges()}
        buttonMessage={`Accept (${filteredBillingDocumentIds().length}) changes`}
      />
      <ConfirmDialog
        title="Accept changes without reversal"
        message={`Accept all changes, including financial changes, on ${staffAcceptAllCount} documents.\n\n• Includes documents already exported to Xero. Xero is not updated.\n• Exported documents go back to Approved and need re-exporting from the Export step.${
          staffAcceptAllSkippedCount
            ? `\n\nNot included (${staffAcceptAllSkippedCount}): documents with a payment assigned, or whose change would remove a document exported to Xero.`
            : ""
        }`}
        isOpen={isConfirmAllDialogOpen}
        onCancel={closeConfirmAllDialog}
        onDelete={() => bulkAcceptChanges(true)}
        buttonMessage={`Accept (${staffAcceptAllCount}) changes`}
      />
      <ConfirmDialog
        title="Reject non-financial changes"
        message={`Please confirm you'd like to reject ${filteredBillingDocumentIds().length} non-financial changes`}
        isOpen={isConfirmRejectCosmeticDialogOpen}
        onCancel={closeConfirmRejectCosmeticDialog}
        onDelete={() => bulkRejectChanges()}
        buttonMessage={`Reject (${filteredBillingDocumentIds().length}) changes`}
      />
      <ConfirmDialog
        title="Reject changes"
        message={`Please confirm you'd like to reject all changes, including financial changes, on ${staffRejectAllCount} documents. This includes documents already exported to Xero. Nothing in Xero will be changed.`}
        isOpen={isConfirmRejectAllDialogOpen}
        onCancel={closeConfirmRejectAllDialog}
        onDelete={() => bulkRejectChanges(true)}
        buttonMessage={`Reject (${staffRejectAllCount}) changes`}
      />
    </WaitForSync>
  );
};
